import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  // Static targets are cameCase
  static targets = ["input", "lat", "lng", "zip", "submit", "error", "googplaceid", "topInstruction"]
  static values = { helpEmail: String }

  connect() {

    // Check if Google Maps API is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      console.log("Google Maps API already loaded")
      this.initAutocomplete()
      return
    }

    // Otherwise, load the Google Maps API script
    this.loadGoogleMaps()
      .then(() => this.initAutocomplete())
      .catch(error => {
        console.error("Failed to load Google Maps API:", error)
        this.showError(`Failed to load Google AutoComplete. Please try again later or contact ${this.helpEmailValue} for help.`)
        this.disableSubmit()
        this.reportError("google_maps", error)
      })
  }

  loadGoogleMaps() {
    // Avoid loading the script multiple times
    if (this.googleMapsLoading) return this.googleMapsLoading

    this.googleMapsLoading = new Promise((resolve, reject) => {
      const script = document.createElement("script")
      const apiKey = document.querySelector("meta[name='g-cfg']")?.content
      if (!apiKey) {
        reject("Missing Google Maps API key")
        return
      }

      window.__initGoogleMaps = () => {
        delete window.__initGoogleMaps
        resolve()
      }

      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=__initGoogleMaps`
      script.async = true
      script.onerror = reject
      document.head.appendChild(script)
    })

    return this.googleMapsLoading
  }

  initAutocomplete() {
    console.log("Initializing Google Maps Autocomplete")
    if (!this.hasInputTarget) return

    const coloradoBounds = new google.maps.LatLngBounds(
      new google.maps.LatLng(37.0, -109.06), // SW corner
      new google.maps.LatLng(41.0, -102.04)  // NE corner
    )

    this._autocomplete = new google.maps.places.Autocomplete(this.inputTarget, {
      types: ["address"],
      componentRestrictions: { country: "us" },
      bounds: coloradoBounds,
      strictBounds: true
    })

    this._autocomplete.addListener('place_changed', this.placeChanged.bind(this))
    this.inputTarget.focus() // Focus the input field on initialization
  }

  placeChanged() {
    const place = this._autocomplete.getPlace()
    if (!place) return

    // Fill in the input with formatted address if available
    if (place.formatted_address) {
      this.inputTarget.value = place.formatted_address
    }

    // Extract latitude and longitude
    if (place.geometry?.location) {
      this.latTarget.value = place.geometry.location.lat()
      this.lngTarget.value = place.geometry.location.lng()
    }

    // Extract postal code (zip)
    let zip = ""
    if (place.address_components) {
      for (const component of place.address_components) {
        if (component.types.includes("postal_code")) {
          zip = component.long_name
          break
        }
      }
    }
    this.zipTarget.value = zip

    // Extract and set google_place_id
    this.googplaceidTarget.value = place.place_id || ""


    // Clear previous error message on successful place selection
    this.clearError()

    // Enable submit button and update styling
    this.enableSubmit()
  }

  validateBeforeSubmit(event) {
    if (!this.latTarget.value) {
      event.preventDefault()
      this.showError("Start typing and select your address from the list that appears below.", { disableInput: false })
    }
  }

  showError(message, { disableInput = true } = {}) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove("hidden")
      this.errorTarget.setAttribute("aria-live", "assertive")
      if (disableInput) {
        this.inputTarget.disabled = true
      }
    } else {
      console.warn("Error target not found in DOM")
    }
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = ""
      this.errorTarget.classList.add("hidden")
    }
  }

  enableSubmit() {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = false
      this.submitTarget.classList.remove("bg-gray-400", "cursor-not-allowed")
      this.submitTarget.classList.add("bg-[#4d733f]", "hover:bg-[#3d5a2f]", "focus:outline-none", "focus:ring-2", "focus:ring-offset-2", "focus:ring-[#4d733f]")
    }

    // Hide the top instruction text
    if (this.hasTopInstructionTarget) {
      this.topInstructionTarget.textContent = ""
    }

    // Update button text
    if (this.hasSubmitTarget) {
      this.submitTarget.value = "Check Your Address"
    }
  }

  disableSubmit() {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true
      this.submitTarget.classList.add("bg-gray-400", "cursor-not-allowed")
      this.submitTarget.classList.remove("bg-[#4d733f]", "hover:bg-[#3d5a2f]", "focus:outline-none", "focus:ring-2", "focus:ring-offset-2", "focus:ring-[#4d733f]")
    }
  }

  reportError(source, error) {
    fetch("/client_errors", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        source: source,
        message: String(error),
        url: window.location.href
      })
    }).catch(() => {})
  }
}